import React, { useCallback, useEffect, useMemo, useState } from 'react';
import intl from 'react-intl-universal';
import SweetAlert from 'react-bootstrap-sweetalert';

import cImageDownload from './img/icon-download.svg';
import { downloadAttachment, fnGetImageAttachment, fnDownloadLocalAttachment } from '../../actions/ActionDocumentDetails';
import { can } from '../../config/Permissions';
import PdfFireViewer from '../../components/PdfFireViewer';

const AttachmentModal = ({ oAttachment, onClose = () => {} }) => {
  if (oAttachment == null) return null;

  const [cImageSource, setImageSource] = useState("");
  const [oPreviewUnavailable, setOPreviewUnavailable] = useState(null);
  const [bIsExpanded, setIsExpanded] = useState(false);

  const cExt = useMemo(() => oAttachment.atch_cname?.split('.').pop().toLowerCase(), [oAttachment]);
  const bIsLocal = useMemo(() => oAttachment.atch_clocal.startsWith("customer\\"), [oAttachment]);

  const fetchAttachmentFile = useCallback(async () => {
    const oData = await fnGetImageAttachment(oAttachment.atch_nid, oAttachment.atch_cname, bIsLocal);
    if (oData?.cType === 'unavailable') {
      setOPreviewUnavailable(oData);
      setImageSource("");
      return;
    }
    setOPreviewUnavailable(null);
    switch (oData.cType) {
      case "base64":
        setImageSource("data:image/png;base64, " + oData.cContent);
        break;
      case "application/pdf":
      default:
        setImageSource(oData.cContent);
        break;
    }
  }, [oAttachment]);

  useEffect(() => {
    setIsExpanded(false);
    fetchAttachmentFile();
  }, [oAttachment]);

  const handleClose = () => {
    onClose();
  };

  if (cImageSource === "" && !oPreviewUnavailable) return null;

  return (
    <SweetAlert
			customClass={`modal-edit modalMedium scrollBar attachment-preview-modal${bIsExpanded ? ' attachment-preview-expanded' : ''}`}
			title=""
      showConfirm={false}
      onConfirm={handleClose}
      onCancel={handleClose}
      closeOnClickOutside
		>
			<div className="head-modal">
        {intl.get("DocumentEditor.view_attachment")}
        {(can('print') || can('admin')) && (
          <span
            role="button"
            tabIndex="0"
            aria-labelledby="head-modal"
            onClick={() => bIsLocal
              ? fnDownloadLocalAttachment(oAttachment.atch_nid, oAttachment.atch_cname)
              : downloadAttachment(oAttachment.atch_nid, oAttachment.atch_cname)
            }
            className="attachment-download"
          >
            <img src={cImageDownload} />
          </span>
        )}
        <span
          role="button"
          tabIndex="0"
          aria-label={intl.get(bIsExpanded ? 'DocumentEditor.reduce_attachment_viewer' : 'DocumentEditor.expand_attachment_viewer')}
          title={intl.get(bIsExpanded ? 'DocumentEditor.reduce_attachment_viewer' : 'DocumentEditor.expand_attachment_viewer')}
          onKeyDown={(oEvent) => {
            if (oEvent.key === 'Enter' || oEvent.key === ' ') {
              oEvent.preventDefault();
              setIsExpanded((bPreviousValue) => !bPreviousValue);
            }
          }}
          onClick={() => setIsExpanded((bPreviousValue) => !bPreviousValue)}
          className="attachment-expand"
        >
          <i className={`fa ${bIsExpanded ? 'fa-compress' : 'fa-expand'}`} aria-hidden="true" />
        </span>
        <span
          role="button"
          tabIndex="0"
          aria-labelledby="head-modal"
          onKeyDown={handleClose}
          onClick={handleClose}
          className="close"
          href=""
        />
      </div>
      {/^(png|jpe?g|bmp)$/i.test(cExt) ? (
        <img className="attachment-image" src={cImageSource} />
      ) : (
        <PdfFireViewer
          pdfBlob={oPreviewUnavailable ? "" : ((cExt !== 'pdf' ? 'data:application/pdf;base64, ' : '') + cImageSource)}
          canDownload={can('print')}
          nameFile={oAttachment.atch_cname}
          downloadUrl={oPreviewUnavailable?.cOriginalUrl || ""}
          downloadFileName={oPreviewUnavailable?.cOriginalFileName || oAttachment.atch_cname}
          fallbackReason={oPreviewUnavailable?.cReason || null}
          showRetry={Boolean(oPreviewUnavailable)}
          onRetry={fetchAttachmentFile}
          cViewerHeight={bIsExpanded ? 'calc(100vh - 180px)' : undefined}
          className="attachment-pdf-viewer"
        />
      )}
		</SweetAlert>
  )
};

export default AttachmentModal;
